import React,{ useRef, useState, useEffect, useCallback } from "react";
import {Box, IconButton} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const PLAYER_WRAPPER_SX = {
    position: 'relative',
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    '@supports not(aspect-ratio)': {
        position: 'absolute',
        top: 0,
        height: '100%'
    }
};


const FALLBACK_SESSION_ID = `tvp_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const buildVTEvent = (video, sessionId, player) => {
    const { currentTime, duration } = player;
    return {
        pg: video.parentId,
        vd: video.id,
        li: video.loginId,
        vvs: sessionId,
        vt: currentTime,
        vct: currentTime,
        vdr: duration
    };
}
const buildVTPEvent = (video, sessionId, player, percentage) => {
    const { currentTime, duration } = player;
    const vtpObject = {
        vd: video.id,
        li: video.loginId,
        vt: currentTime,
        vvs: sessionId,
        vct: currentTime,
        vdr: duration,
        vtp: percentage
    }
    return vtpObject;
}

const getVideoSource = (asset) =>{
    if(asset?.sources?.length > 0){
        const sources = [...asset.sources]
        let sortedSource = sources.sort((a, b)=>{
            if(a.width < b.width){
                return 1
            }else if(a.width > b.width){
                return -1
            }
            return 0
        });
        const chosen = sortedSource[1] || sortedSource[0];
        return chosen ? {...chosen, file: chosen.file || chosen.url} : null;
    }
    return null

}

const NativePlayer = (props) => {
    const playerRef = useRef(null);
    const { video, autoplay = false, handleCloseModal, isLandscape = false, active = false, index = 0, activePosition = 0 } = props;
    const sessionId = props.sessionId || FALLBACK_SESSION_ID;
    const [currentlyPlaying, setCurrentlyPlaying] = useState(video.id);
    const [initialized, setInitialized] = useState(false);
    const [vvEventTriggered, setVvEventTriggered] = useState(false);
    const [muted, setMuted] = useState(false);

    useEffect(() => {
        if (currentlyPlaying !== video.id) {
            setCurrentlyPlaying(video.id);
            setVvEventTriggered(false);
            const current = playerRef.current;
            if (current) {
                clearGlobalSessionId();
                current.load();
            }
        }
    }, [video, sessionId]);

    useEffect(() => {
        if(!playerRef?.current){
            return;
        }
        const handlePlayMute = () =>{
            if(playerRef?.current){
                playerRef.current.muted = true;
                setMuted(true);
                playerRef?.current?.play();
            }
        }
        if(active && autoplay && playerRef){
            playerRef?.current?.play().catch(()=>{
                handlePlayMute();
            });
        }else if(playerRef){
            playerRef?.current?.pause();
            // @ts-ignore
            playerRef.current.currentTime = 0;
        }
    }, [active, autoplay, playerRef]);

    useEffect(() => {
        if (playerRef.current) {
            playerRef.current.muted = muted;
        }
    }, [muted]);

    useEffect(() => {
        const toTheLeft = activePosition - 3;
        const toTheRight = activePosition + 3;
        const shouldLoad = index >= toTheLeft && index <= toTheRight;
        if (shouldLoad) {
            setInitialized(true);
        }else{
            setInitialized(false);
        }
    }, [index, activePosition])


    const clearGlobalSessionId = useCallback(() => {
        window[sessionId] = null;
    }, [sessionId])

    const timeElapsed = useCallback(() => {

        if (playerRef.current) {
            const { currentTime, duration } = playerRef.current;
            const percentage = Math.floor((currentTime * 100) / duration);
            if (!vvEventTriggered) {
                setVvEventTriggered(true);
                const analyticsObj = {
                    pg: video.parentId,
                    vd: video.id,
                    li: video.loginId,
                    vvs: sessionId
                };
                window?._tvpa?.push(['track', 'vv', analyticsObj]);
            }
            const eventRegistered = window[sessionId];
            if (!eventRegistered) {
                window[sessionId] = {};
            }

            if ((Math.floor(currentTime) % 3) === 0 && Math.floor(currentTime) > 0) {
                const timeEvaluate = Math.floor(currentTime);
                if (!window[sessionId][timeEvaluate]) {
                    window[sessionId][timeEvaluate] = true;
                    window?._tvpa?.push(['track', 'vt', buildVTEvent(video, sessionId, playerRef.current)]);
                }
            }
            const generatePercentageEvent = (value, player) => {
                if (!window[sessionId][value]) {
                    window[sessionId][value] = true;
                    window?._tvpa?.push(['track', 'vtp', buildVTPEvent(video, sessionId, player, value)]);
                }
            }
            if (percentage >= 25 && percentage < 50) {
                generatePercentageEvent(25, playerRef.current);
            }
            if (percentage >= 50 && percentage < 75) {
                generatePercentageEvent(50, playerRef.current);
            }
            if (percentage >= 75 && percentage < 97) {
                generatePercentageEvent(75, playerRef.current);
            }
            if (percentage >= 97 && percentage <= 100) {
                generatePercentageEvent(100, playerRef.current);
            }
        }
    }, [video, vvEventTriggered, setVvEventTriggered, sessionId])

    useEffect(() => {
        const player = playerRef.current;
        if (!player) return;

        const handleTimeUpdate = timeElapsed;

        const handleEnded = () => {
            setVvEventTriggered(false);
            player?.play();
            clearGlobalSessionId();
        };

        const cleanupCallback = () => {
            clearGlobalSessionId();
            if (player) {
                player.removeEventListener("timeupdate", handleTimeUpdate);
                player.removeEventListener("ended", handleEnded);
            }
        };

        if (initialized) {
            player.addEventListener("timeupdate", handleTimeUpdate);
            player.addEventListener("ended", handleEnded);
        }

        return () => {
            cleanupCallback();
        };
    }, [playerRef, initialized, timeElapsed, clearGlobalSessionId]);


    const playPauseHandler = () =>{
        if(playerRef?.current){
            if(playerRef.current.paused){
                playerRef.current.play();
            }else{
                playerRef.current.pause();
            }
        }
    }

    const source = getVideoSource(video.asset);
    return <Box id={"creatable-video-wrapper"} style={isLandscape ?{height: '100%', display:'flex'} : {}} sx={PLAYER_WRAPPER_SX}>
            {source && (
                <video
                    ref={playerRef}
                    width="100%"
                    height={"auto"}
                    controls
                    playsInline
                    preload={"metadata"}
                >
                    <source src={source.file} type={source.mime}></source>
                </video>
            )}
        <Box
            component="button"
            type="button"
            onClick={playPauseHandler}
            sx={{
                position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px',
                overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
                '&:focus-visible': {
                    position: 'static', width: 'auto', height: 'auto', margin: 0, clip: 'auto',
                    padding: '8px 12px', background: '#000', color: '#fff', zIndex: 1001,
                },
            }}
        >
            Play or pause video
        </Box>
        {handleCloseModal && (
            <Box display={'flex'} position={'absolute'} top={"15px"} left={0} px={2} zIndex={'1000'}>
                <IconButton aria-label="Close video" sx={{'&:hover':{backgroundColor: 'inherit'}, cursor: 'pointer', display: { xs: 'flex', sm: 'none'}, width: 'auto'}} onClick={handleCloseModal} disableRipple>
                    <CloseIcon htmlColor={'#ffffff'} sx={{fill: '#ffffff'}} />
                </IconButton>
            </Box>
        )}
    </Box>
}

export default NativePlayer;
